import { useAsyncFn } from 'react-use'
import { useCallback, useContext } from 'react'
import { Moises } from '../../lib/cli'
import { config } from '../../config'
import { useFirebaseToken } from '../auth/use-firebase-token'
import { ContextShell } from '../../modules/context'

export type Announcement = {
  lang?: string | null
  image?: string | null
  title?: string | null
  name?: string | null
  tagLabel?: string | null
  button?: {
    url?: string | null
    label?: string | null
    newTab?: boolean
  }
  description?: string | null
  startAt?: string | null
  id?: string | null
}

type UseAnnouncements = {
  loading: boolean
  error?: Error | undefined
  value?: Announcement | undefined
  loadAnnouncements: () => Promise<Announcement | undefined>
  dismissAnnouncement: ({
    id,
    elements
  }: {
    id: string
    elements: string[]
  }) => Promise<void>
}

const MoisesCLI = Moises({ apiEndpoint: config.api.endpoint })

const isValidAnnouncement = (response: any, user: any): boolean => {
  if (!response?.announcement?.length) {
    return false
  }

  if (response?.announcement?.[0]?.name === 'Moises Live Launch') {
    if (user?.isEmployee) {
      return true
    }

    if (typeof window === 'undefined') {
      return false
    }

    // @ts-ignore
    const isWindows = window?.navigator?.userAgentData?.platform
      ?.toLowerCase()
      ?.includes('win')

    if (!isWindows) {
      return false
    }
  }

  return true
}

export const useAnnouncements = (): UseAnnouncements => {
  const userToken = useFirebaseToken()
  const { user } = useContext(ContextShell)

  const [state, loadAnnouncements] = useAsyncFn(async () => {
    if (userToken) {
      MoisesCLI.auth(userToken)

      const response = await MoisesCLI.getAnnouncements()

      if (!isValidAnnouncement(response, user)) {
        return undefined
      }

      return {
        ...(response.announcement?.[0]?.cmsPayload?.web?.sideBanner?.[0] || {}),
        id: response.announcement?.[0]?.id,
        startAt: response.announcement?.[0]?.startAt,
        name: response.announcement?.[0]?.name
      }
    }

    return undefined
  }, [userToken, user])

  const dismissAnnouncement = useCallback(
    async ({ id, elements }: { id: string; elements: string[] }) => {
      if (userToken) {
        MoisesCLI.auth(userToken)
        const response = await MoisesCLI.dismissAnnouncement({
          id,
          elements
        })
        return response
      }
      return {}
    },
    [userToken]
  )

  return {
    ...state,
    dismissAnnouncement,
    loadAnnouncements
  }
}
